import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, Database, Loader2, TrendingUp } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface AutoMigrationStats {
  totalCourses: number;
  totalModules: number;
  coursesByCategory: Record<string, number>;
  migrationSuccess: boolean;
}

const AutoMigrationRunner: React.FC = () => {
  const [stats, setStats] = useState<AutoMigrationStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [migrationComplete, setMigrationComplete] = useState(false);

  const runAutoMigration = async () => {
    try {
      console.log('🚀 Auto-running course migration...');
      
      // Call the migration edge function
      const { data, error } = await supabase.functions.invoke('migrate-courses');
      
      if (error) {
        // Silent fail - migration is admin-only and non-critical for SEO
        return;
      }

      if (data.success) {
        console.log('✅ Auto-migration completed successfully!');
        console.log('Migration results:', data.migration_results);
        console.log('Final stats:', data.final_stats);
        
        setStats({
          totalCourses: data.final_stats.total_courses,
          totalModules: data.final_stats.total_modules,
          coursesByCategory: data.final_stats.courses_by_category,
          migrationSuccess: true
        });
        setMigrationComplete(true);
      }
    } catch (error) {
      console.error('Auto-migration failed:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Auto-run migration on component mount
    runAutoMigration();
  }, []);

  if (loading) {
    return (
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
            Auto-Migration in Progress
          </CardTitle>
          <CardDescription>
            Automatically transferring legacy courses to database...
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
              <p className="text-muted-foreground">Processing course migration...</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-6 border-green-200">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-green-600" />
          Auto-Migration Complete! 🎉
        </CardTitle>
        <CardDescription>
          Legacy courses have been successfully transferred to the database
        </CardDescription>
      </CardHeader>
      <CardContent>
        {migrationComplete && stats && (
          <div className="space-y-4">
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                <strong>Migration Successful!</strong> All legacy courses have been automatically 
                transferred to the database with full module structure preserved.
              </AlertDescription>
            </Alert>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-primary/10 rounded-lg">
                <div className="text-2xl font-bold text-primary">{stats.totalCourses}</div>
                <div className="text-sm text-muted-foreground">Total Courses Migrated</div>
              </div>
              <div className="text-center p-4 bg-secondary/10 rounded-lg">
                <div className="text-2xl font-bold text-secondary-foreground">{stats.totalModules}</div>
                <div className="text-sm text-muted-foreground">Total Modules</div>
              </div>
              <div className="text-center p-4 bg-accent/10 rounded-lg">
                <div className="text-2xl font-bold text-accent-foreground">
                  {Object.keys(stats.coursesByCategory).length}
                </div>
                <div className="text-sm text-muted-foreground">Active Categories</div>
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="font-semibold flex items-center gap-2">
                <Database className="h-4 w-4" />
                Courses by Category
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {Object.entries(stats.coursesByCategory)
                  .sort(([,a], [,b]) => b - a)
                  .map(([category, count]) => (
                    <div key={category} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                      <span className="text-xs font-medium capitalize">
                        {category.replace('-', ' ')}
                      </span>
                      <Badge variant="secondary" className="text-xs">{count}</Badge>
                    </div>
                  ))}
              </div>
            </div>

            <Alert className="border-blue-200 bg-blue-50">
              <TrendingUp className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-800">
                <strong>System Upgraded:</strong> Your platform now uses a database-driven course system 
                with enhanced search, filtering, and real-time updates. Legacy file-based courses 
                have been successfully migrated.
              </AlertDescription>
            </Alert>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AutoMigrationRunner;